import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useAuthStore } from '../store/authStore';
import { useAuthInitialized } from './AuthProvider';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Gate for the vendor dashboard. Requires an authenticated user whose role is
 * "vendor" and who has been approved by an admin. Otherwise shows an
 * application / pending-approval screen.
 */
export function RequireVendor({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { initialized } = useAuthInitialized();
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user && user.role === 'vendor' && !user.is_vendor_approved) {
      // refetch profile so approval status is fresh
      useAuthStore.getState().fetchUserProfile();
    }
  }, [isAuthenticated, user]);

  const refreshProfile = () => useAuthStore.getState().fetchUserProfile();

  // Wait for keycloak.init() to finish before deciding. Redirecting to /login
  // first strips the ?code= callback param and breaks the PKCE exchange.
  if (!initialized || isLoading) {
    return (
      <div className="loading-spinner">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return (
      <div className="loading-spinner">
        <div className="spinner" />
      </div>
    );
  }

  if (user.role === 'admin') {
    // Admins may preview the vendor dashboard.
    return <>{children}</>;
  }

  if (user.role === 'vendor' && user.is_vendor_approved) {
    return <>{children}</>;
  }

  const apply = async () => {
    setApplying(true);
    try {
      await api.apply.apply();
      toast.success('Application submitted for admin review.');
      await refreshProfile();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="container">
      <div className="panel" style={{ maxWidth: '640px', margin: '4rem auto' }}>
        <h2>Vendor Access Required</h2>
        {user.role === 'vendor' && !user.is_vendor_approved ? (
          <>
            <p className="page-subtitle">
              Your vendor application is <strong>pending approval</strong>. An admin will review
              your account shortly. Once approved, you can upload and manage assets.
            </p>
            <div className="empty-state">
              <button type="button" className="btn btn--secondary" onClick={refreshProfile}>
                Check Approval Status
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="page-subtitle">
              You need vendor access to manage assets on the Ludic Drive Assetstore. Submit an
              application and an admin will review it.
            </p>
            <div className="empty-state">
              <button
                type="button"
                className="btn btn--primary"
                disabled={applying}
                onClick={apply}
              >
                {applying ? 'Submitting…' : 'Apply to Become a Vendor'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
